"use client";

import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Loader2,
  Plus,
  DollarSign,
  Trophy,
  FileText,
  ArrowDownCircle,
  ArrowUpCircle,
  AlertCircle,
  ShieldAlert,
  X,
  ChevronDown,
} from "lucide-react";
import { toast } from "react-hot-toast";

export function InlineTournamentHouseFeeAddBar({
  onAddTransaction,
  currentBalance = 0,
  transactionDetails = [],
  isLoading = false,
}) {
  const [transactionType, setTransactionType] = useState("withdraw"); // "withdraw" | "deposit"
  const [amount, setAmount] = useState("");
  const [selectedPreset, setSelectedPreset] = useState("");
  const [presetSearch, setPresetSearch] = useState("");
  const [isPresetDropdownOpen, setIsPresetDropdownOpen] = useState(false);
  const [highlightedPresetIndex, setHighlightedPresetIndex] = useState(0);

  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [presetOptions, setPresetOptions] = useState(transactionDetails || []);

  // Input refs for keyboard chaining
  const amountInputRef = useRef(null);
  const presetInputRef = useRef(null);
  const presetDropdownRef = useRef(null);
  const presetListRef = useRef(null);
  const notesInputRef = useRef(null);

  const isDeposit = transactionType === "deposit";
  const numericAmount = parseFloat(amount) || 0;
  const isInsufficientFunds = !isDeposit && numericAmount > currentBalance;
  const isBelowMinBalance = currentBalance < 2000;

  // Load preset options from prop or localStorage
  useEffect(() => {
    const saved = localStorage.getItem("transactionDetailstournamenthouse");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setPresetOptions(parsed);
          return;
        }
      } catch (_) {
        /* ignore */
      }
    }
    if (Array.isArray(transactionDetails)) {
      setPresetOptions(transactionDetails);
    }
  }, [transactionDetails]);

  // Filter presets based on search
  const filteredPresets = useMemo(() => {
    if (!presetSearch.trim()) return presetOptions;
    const term = presetSearch.trim().toLowerCase();
    return presetOptions.filter((p) => p.toLowerCase().includes(term));
  }, [presetOptions, presetSearch]);

  // Reset highlight index when filtered presets change
  useEffect(() => {
    setHighlightedPresetIndex(0);
  }, [filteredPresets.length]);

  // Auto-scroll dropdown container so highlighted item is always in view
  useEffect(() => {
    if (isPresetDropdownOpen && presetListRef.current) {
      const container = presetListRef.current;
      const highlightedEl = container.querySelector(
        `[data-index="${highlightedPresetIndex}"]`
      );
      if (highlightedEl) {
        const containerTop = container.scrollTop;
        const containerBottom = containerTop + container.clientHeight;
        const elTop = highlightedEl.offsetTop;
        const elBottom = elTop + highlightedEl.offsetHeight;

        if (elTop < containerTop) {
          container.scrollTop = elTop;
        } else if (elBottom > containerBottom) {
          container.scrollTop = elBottom - container.clientHeight;
        }
      }
    }
  }, [highlightedPresetIndex, isPresetDropdownOpen]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (presetDropdownRef.current && !presetDropdownRef.current.contains(e.target)) {
        setIsPresetDropdownOpen(false);
        setPresetSearch("");
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const selectPreset = useCallback((preset) => {
    setSelectedPreset(preset);
    setNotes(preset);
    setPresetSearch("");
    setIsPresetDropdownOpen(false);
    // Smoothly focus Notes input next for adjustments
    setTimeout(() => {
      notesInputRef.current?.focus();
    }, 50);
  }, []);

  const handleTypeChange = (type) => {
    setTransactionType(type);
    setTimeout(() => {
      amountInputRef.current?.focus();
    }, 50);
  };

  // Keyboard navigation for Amount Input
  const handleAmountKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (!amount || isNaN(numericAmount) || numericAmount <= 0) {
        toast.error("Please enter a valid amount");
        return;
      }

      if (!isDeposit && numericAmount > currentBalance) {
        toast.error("Insufficient funds for this withdrawal");
        return;
      }

      // If preset options exist, advance to preset dropdown
      if (presetOptions.length > 0) {
        presetInputRef.current?.focus();
        setIsPresetDropdownOpen(true);
      } else {
        notesInputRef.current?.focus();
      }
    }
  };

  // Keyboard navigation for Preset Dropdown
  const handlePresetKeyDown = (e) => {
    if (isPresetDropdownOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (filteredPresets.length > 0) {
          setHighlightedPresetIndex((prev) => (prev + 1) % filteredPresets.length);
        }
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        if (filteredPresets.length > 0) {
          setHighlightedPresetIndex((prev) =>
            prev <= 0 ? filteredPresets.length - 1 : prev - 1
          );
        }
        return;
      }
      if (e.key === "PageDown") {
        e.preventDefault();
        if (filteredPresets.length > 0) {
          setHighlightedPresetIndex((prev) =>
            Math.min(filteredPresets.length - 1, prev + 5)
          );
        }
        return;
      }
      if (e.key === "PageUp") {
        e.preventDefault();
        if (filteredPresets.length > 0) {
          setHighlightedPresetIndex((prev) => Math.max(0, prev - 5));
        }
        return;
      }
      if (e.key === "Home") {
        e.preventDefault();
        if (filteredPresets.length > 0) {
          setHighlightedPresetIndex(0);
        }
        return;
      }
      if (e.key === "End") {
        e.preventDefault();
        if (filteredPresets.length > 0) {
          setHighlightedPresetIndex(filteredPresets.length - 1);
        }
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        if (filteredPresets.length > 0) {
          e.preventDefault();
          const selected = filteredPresets[highlightedPresetIndex] || filteredPresets[0];
          selectPreset(selected);
          return;
        } else if (presetSearch.trim()) {
          e.preventDefault();
          setNotes(presetSearch.trim());
          setSelectedPreset(presetSearch.trim());
          setIsPresetDropdownOpen(false);
          setPresetSearch("");
          notesInputRef.current?.focus();
          return;
        }
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setIsPresetDropdownOpen(false);
        setPresetSearch("");
        return;
      }
    }

    if (e.key === "Enter") {
      e.preventDefault();
      if (selectedPreset) {
        notesInputRef.current?.focus();
      } else {
        setIsPresetDropdownOpen(true);
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setIsPresetDropdownOpen(true);
    }
  };

  // Keyboard navigation for Notes Input
  const handleNotesKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!amount || isNaN(numericAmount) || numericAmount <= 0) {
      toast.error("Please enter a valid amount");
      amountInputRef.current?.focus();
      return;
    }

    if (!isDeposit && numericAmount > currentBalance) {
      toast.error("Insufficient funds for this withdrawal");
      amountInputRef.current?.focus();
      return;
    }

    if (!notes.trim()) {
      toast.error("Please provide transaction details or select a preset");
      notesInputRef.current?.focus();
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        transactionType,
        amount: numericAmount,
        notes: notes.trim(),
      };

      const success = await onAddTransaction(payload);
      if (success) {
        // Reset form fields
        setAmount("");
        setSelectedPreset("");
        setPresetSearch("");
        setNotes("");
        setIsPresetDropdownOpen(false);

        // Return focus to amount input ready for next entry
        setTimeout(() => {
          amountInputRef.current?.focus();
        }, 80);
      }
    } catch (error) {
      console.error("Inline tournament house fee submit error:", error);
      toast.error(error.message || "Failed to add transaction");
    } finally {
      setSubmitting(false);
    }
  };

  const busy = submitting || isLoading;
  const isFormValid =
    numericAmount > 0 &&
    (isDeposit || !isInsufficientFunds) &&
    Boolean(notes.trim());

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-5 mb-8 transition-all">
      {/* Header bar: Title, Type Switcher & Live Balance */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 pb-3 border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500 animate-pulse" />
          <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide">
            Add Tournament House Fee
          </h2>
          <span className="hidden md:flex items-center gap-1 text-[11px] text-gray-400 ml-2">
            <span>Press</span>
            <kbd className="px-1.5 py-0.5 rounded bg-gray-100 border border-gray-300 text-[10px] font-mono text-gray-700">
              Enter ↵
            </kbd>
            <span>to navigate / save</span>
          </span>
        </div>

        <div className="flex items-center gap-3">
          {/* Segmented Type Toggle */}
          <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50 text-xs font-medium">
            <button
              type="button"
              onClick={() => handleTypeChange("withdraw")}
              disabled={busy}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all ${
                !isDeposit
                  ? "bg-rose-500 text-white font-semibold shadow-xs"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <ArrowDownCircle size={14} />
              <span>Withdrawal</span>
            </button>
            <button
              type="button"
              onClick={() => handleTypeChange("deposit")}
              disabled={busy}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all ${
                isDeposit
                  ? "bg-emerald-600 text-white font-semibold shadow-xs"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <ArrowUpCircle size={14} />
              <span>Deposit</span>
            </button>
          </div>

          {/* Balance Pill with Min $2,000 threshold indicator */}
          <div
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${
              isInsufficientFunds
                ? "bg-rose-50 border-rose-300 text-rose-700 animate-pulse"
                : isBelowMinBalance
                ? "bg-amber-50 border-amber-300 text-amber-800"
                : "bg-blue-50 border-blue-200 text-blue-700"
            }`}
          >
            {isBelowMinBalance ? (
              <ShieldAlert size={13} className="text-amber-600 shrink-0" />
            ) : null}
            <span className="text-[10px] uppercase tracking-wider opacity-75">
              Available
            </span>
            <span>
              ${Number(currentBalance || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
        </div>
      </div>

      {/* Insufficient Funds Warning */}
      {isInsufficientFunds && (
        <div className="mb-3.5 p-2.5 rounded-lg bg-rose-50 border border-rose-200 flex items-center gap-2 text-xs text-rose-700">
          <AlertCircle size={15} className="shrink-0 text-rose-500" />
          <span>
            Requested withdrawal of <strong>${numericAmount.toFixed(2)}</strong> exceeds available balance of{" "}
            <strong>${Number(currentBalance || 0).toFixed(2)}</strong>.
          </span>
        </div>
      )}

      {/* Form Fields */}
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col lg:flex-row items-stretch lg:items-end gap-3">
          {/* 1. Amount */}
          <div className="w-full lg:w-44 shrink-0">
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Amount <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                <DollarSign size={15} />
              </div>
              <Input
                ref={amountInputRef}
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                onKeyDown={handleAmountKeyDown}
                disabled={busy}
                className={`pl-8 text-sm font-semibold h-10 ${
                  isInsufficientFunds ? "border-rose-400 focus:ring-rose-400" : ""
                }`}
              />
            </div>
          </div>

          {/* 2. Preset Selection Searchable Dropdown with Arrow Key Nav */}
          <div className="w-full lg:w-60 shrink-0 relative" ref={presetDropdownRef}>
            <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center justify-between">
              <span className="flex items-center gap-1">
                <Trophy size={12} className="text-gray-400" />
                <span>Preset Detail</span>
              </span>
              <span className="text-[10px] text-gray-400 font-normal">
                (↑/↓ to navigate)
              </span>
            </label>

            <div className="relative">
              <Input
                ref={presetInputRef}
                type="text"
                value={isPresetDropdownOpen ? presetSearch : selectedPreset}
                placeholder="Choose or search preset..."
                onFocus={() => {
                  setPresetSearch(selectedPreset);
                  setIsPresetDropdownOpen(true);
                }}
                onChange={(e) => {
                  setPresetSearch(e.target.value);
                  setIsPresetDropdownOpen(true);
                }}
                onKeyDown={handlePresetKeyDown}
                disabled={busy}
                className="pr-8 text-xs h-10"
              />
              <div className="absolute inset-y-0 right-0 pr-2.5 flex items-center gap-1">
                {selectedPreset ? (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedPreset("");
                      setNotes("");
                      setPresetSearch("");
                      presetInputRef.current?.focus();
                    }}
                    className="text-gray-400 hover:text-gray-600 p-0.5"
                  >
                    <X size={13} />
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => setIsPresetDropdownOpen((prev) => !prev)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <ChevronDown size={14} />
                </button>
              </div>
            </div>

            {/* Presets Dropdown List with Highlight & Scroll */}
            {isPresetDropdownOpen && (
              <div
                ref={presetListRef}
                className="absolute z-50 mt-1 w-full bg-white rounded-lg shadow-xl border border-gray-200 max-h-56 overflow-y-auto py-1"
              >
                {filteredPresets.length > 0 ? (
                  filteredPresets.map((preset, index) => {
                    const isHighlighted = index === highlightedPresetIndex;
                    const isSelected = selectedPreset === preset;

                    return (
                      <button
                        key={`${preset}-${index}`}
                        data-index={index}
                        type="button"
                        onClick={() => selectPreset(preset)}
                        onMouseEnter={() => setHighlightedPresetIndex(index)}
                        className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between transition-colors ${
                          isHighlighted
                            ? "bg-gray-100 text-gray-900 font-semibold"
                            : isSelected
                            ? "bg-yellow-50 text-yellow-900 font-medium"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        <span className="truncate">{preset}</span>
                        {isSelected && (
                          <span className="text-[10px] text-yellow-600 bg-yellow-100 px-1.5 py-0.5 rounded font-semibold shrink-0">
                            Selected
                          </span>
                        )}
                      </button>
                    );
                  })
                ) : (
                  <div className="px-3 py-2 text-xs text-gray-400 text-center">
                    No matching presets found
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 3. Transaction Details / Notes */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
              <FileText size={12} className="text-gray-400" />
              <span>Transaction Details</span>
              <span className="text-red-500">*</span>
            </label>
            <Input
              ref={notesInputRef}
              type="text"
              placeholder="Enter details or choose preset..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onKeyDown={handleNotesKeyDown}
              disabled={busy}
              className="text-sm h-10"
            />
          </div>

          {/* 4. Submit Button */}
          <div className="shrink-0 pt-1 lg:pt-0">
            <Button
              type="submit"
              disabled={busy || !isFormValid}
              className={`w-full lg:w-auto h-10 px-5 font-semibold text-xs uppercase tracking-wider transition-all shadow-xs ${
                isDeposit
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                  : "bg-yellow-400 hover:bg-yellow-500 text-black"
              }`}
            >
              {busy ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  <span>Adding...</span>
                </>
              ) : (
                <>
                  <Plus className="mr-1.5 h-4 w-4 stroke-[2.5]" />
                  <span>{isDeposit ? "Add Deposit" : "Add Withdrawal"}</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
